import { useEffect, useRef, useState } from 'react';

import {
  Activity,
  BarChart3,
  BookMarked,
  Check,
  ChevronDown,
  ChevronUp,
  GripHorizontal,
  KanbanSquare,
  Loader2,
  Pencil,
  PieChart,
  Plus,
  SendHorizontal,
  Sparkles,
  Table2,
  Trash2,
  TrendingUp,
  X,
} from 'lucide-react';
import { motion, useDragControls } from 'motion/react';
import { useTranslation } from 'react-i18next';

import { Bubble, BubbleContent, BubbleGroup } from '@/components/ui/bubble';
import { Button } from '@/components/ui/button';
import {
  Message,
  MessageAvatar,
  MessageContent,
  MessageGroup,
  MessageHeader,
} from '@/components/ui/message';
import { Separator } from '@/components/ui/separator';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';

import { useApplyElementChanges } from '../hooks/use-apply-element-changes';
import { useCreateFormulaEntry } from '../hooks/use-create-formula-entry';
import { useGenerateReportFromAi } from '../hooks/use-generate-report-from-ai';
import { mapAiChangesToApplyBody } from '../mappers/ai-element-changes.mapper';
import { Report } from '../types';
import {
  AiApplyState,
  AiChatHistoryMessage,
  AiElementChange,
  AiElementChangeAction,
  AiGeneratedFormula,
  ChatMessage,
} from '../types/ai-report';
import { ReportAiClarificationCard } from './report-ai-clarification-card';
import { ReportAiPlanSummary } from './report-ai-plan-summary';

interface ReportAiChatProps {
  report: Report;
  onClose: () => void;
}

const ELEMENT_ICONS: Record<string, React.ElementType> = {
  TABLE: Table2,
  BAR_CHART: BarChart3,
  LINE_CHART: TrendingUp,
  AREA_CHART: Activity,
  PIE_CHART: PieChart,
  KPI_CARD: KanbanSquare,
};

/** Backend limit on the conversation length sent per request. */
const MAX_HISTORY_MESSAGES = 20;

const toHistory = (messages: ChatMessage[]): AiChatHistoryMessage[] =>
  messages
    .flatMap((msg): AiChatHistoryMessage[] => {
      if (msg.role === 'user') return [{ role: 'USER', content: msg.content }];
      if (msg.role === 'error') return [];
      return [{ role: 'ASSISTANT', content: msg.assistantMessage }];
    })
    .slice(-MAX_HISTORY_MESSAGES);

const CHANGE_ICONS: Record<AiElementChangeAction, React.ElementType> = {
  ADD: Plus,
  UPDATE: Pencil,
  DELETE: Trash2,
};

interface AiChangeCardProps {
  change: AiElementChange;
  report: Report;
}

const AiChangeCard = ({ change, report }: AiChangeCardProps) => {
  const { t } = useTranslation('authenticated.reports');
  const { action, element, elementId } = change;
  const ChangeIcon = CHANGE_ICONS[action];
  const current = report.elements.find(el => el.id === elementId);
  const Icon = ELEMENT_ICONS[element?.elementType ?? current?.type ?? ''] ?? BarChart3;

  const title =
    element?.title ||
    (current?.config.title as string | undefined) ||
    (current ? t(`element.${current.type}`) : t('ai.unknownElement'));

  return (
    <div className="flex items-start gap-2 rounded-md border bg-background px-3 py-2">
      <Icon className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
      <div className="min-w-0 flex-1">
        <p className={cn('text-sm font-medium', action === 'DELETE' && 'line-through')}>{title}</p>
        {element && (
          <p className="text-xs text-muted-foreground">
            {t('ai.elementSubtitle', {
              element: t(`element.${element.elementType}`),
              dataset: t(`dataset.${element.reportType}`),
            })}
          </p>
        )}
        {element && element.columns.length > 0 && (
          <p className="mt-0.5 truncate text-xs text-muted-foreground">
            {element.columns.join(', ')}
          </p>
        )}
      </div>
      <span className="flex shrink-0 items-center gap-1 text-xs text-muted-foreground">
        <ChangeIcon className="h-3.5 w-3.5" />
        {t(`ai.changeAction.${action}`)}
      </span>
    </div>
  );
};

interface AiApplyButtonProps {
  applyState: AiApplyState;
  onApply: () => void;
}

const AiApplyButton = ({ applyState, onApply }: AiApplyButtonProps) => {
  const { t } = useTranslation('authenticated.reports');
  const isApplied = applyState === 'applied';
  const isApplying = applyState === 'applying';

  return (
    <Button
      size="sm"
      variant={isApplied ? 'secondary' : 'default'}
      className="h-8 w-full text-xs"
      disabled={isApplied || isApplying}
      onClick={onApply}
    >
      {isApplying ? (
        <Loader2 className="mr-1 h-3 w-3 animate-spin" />
      ) : isApplied ? (
        <Check className="mr-1 h-3 w-3" />
      ) : null}
      {isApplied ? t('ai.applied') : t('ai.applyChanges')}
    </Button>
  );
};

interface AiFormulaCardProps {
  formula: AiGeneratedFormula;
  savedKeys: Set<string>;
  onSaved: (key: string) => void;
}

const AiFormulaCard = ({ formula, savedKeys, onSaved }: AiFormulaCardProps) => {
  const { t } = useTranslation('authenticated.reports');
  const { mutate: createFormula, isPending } = useCreateFormulaEntry();
  const isSaved = savedKeys.has(formula.key);

  const handleSave = () => {
    createFormula(
      {
        scope: 'USER',
        key: formula.key,
        label: formula.label,
        formulaString: formula.formulaString,
        dependsOn: formula.dependsOn,
        measureType: formula.measureType,
      },
      { onSuccess: () => onSaved(formula.key) }
    );
  };

  return (
    <div className="rounded-md border bg-background p-3">
      <div className="flex items-start gap-2">
        <BookMarked className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium">{formula.label}</p>
          <p className="mt-0.5 truncate font-mono text-xs text-muted-foreground">
            {formula.formulaString}
          </p>
          <p className="text-xs text-muted-foreground">
            {formula.measureType}
            {formula.unit ? ` · ${formula.unit}` : ''}
          </p>
        </div>
      </div>
      <Button
        size="sm"
        variant={isSaved ? 'secondary' : 'outline'}
        className="mt-2 h-7 w-full text-xs"
        disabled={isSaved || isPending}
        onClick={handleSave}
      >
        {isPending ? (
          <Loader2 className="mr-1 h-3 w-3 animate-spin" />
        ) : isSaved ? (
          <Check className="mr-1 h-3 w-3" />
        ) : (
          <BookMarked className="mr-1 h-3 w-3" />
        )}
        {isSaved ? t('ai.formulaSaved') : t('ai.addFormula')}
      </Button>
    </div>
  );
};

/** The input grows with its text up to this height, then scrolls. */
const INPUT_MAX_HEIGHT = 160;

const MIN_W = 280;
const MIN_H = 320;
const DEFAULT_W = 320;
const DEFAULT_H = 560;

export const ReportAiChat = ({ report, onClose }: ReportAiChatProps) => {
  const { t } = useTranslation('authenticated.reports');
  const dragControls = useDragControls();

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [isMinimized, setIsMinimized] = useState(false);
  const [savedFormulaKeys, setSavedFormulaKeys] = useState<Set<string>>(new Set());
  const [size, setSize] = useState({ width: DEFAULT_W, height: DEFAULT_H });
  const bottomRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const isMountedRef = useRef(true);
  const resizeListenersRef = useRef<{ onMove: (e: PointerEvent) => void; onUp: () => void } | null>(
    null
  );
  const { mutate: generate, isPending } = useGenerateReportFromAi();
  const { mutate: applyChanges } = useApplyElementChanges();

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      if (resizeListenersRef.current) {
        window.removeEventListener('pointermove', resizeListenersRef.current.onMove);
        window.removeEventListener('pointerup', resizeListenersRef.current.onUp);
        resizeListenersRef.current = null;
      }
    };
  }, []);

  // Auto-grow: measure the content height after every change, including the reset after sending.
  useEffect(() => {
    const textarea = inputRef.current;
    if (!textarea) return;
    textarea.style.height = 'auto';
    textarea.style.height = `${Math.min(textarea.scrollHeight, INPUT_MAX_HEIGHT)}px`;
    textarea.style.overflowY = textarea.scrollHeight > INPUT_MAX_HEIGHT ? 'auto' : 'hidden';
  }, [input, isMinimized]);

  const scrollToBottom = () => {
    setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
  };

  const handleResizeMouseDown = (e: React.PointerEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startY = e.clientY;
    const startW = size.width;
    const startH = size.height;

    const onMove = (ev: PointerEvent) => {
      setSize({
        width: Math.max(MIN_W, startW + (ev.clientX - startX)),
        height: Math.max(MIN_H, startH + (ev.clientY - startY)),
      });
    };
    const onUp = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      resizeListenersRef.current = null;
    };
    resizeListenersRef.current = { onMove, onUp };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  };

  const setApplyState = (messageId: string, applyState: AiApplyState) => {
    setMessages(prev =>
      prev.map(msg =>
        msg.id === messageId && msg.role === 'report' ? { ...msg, applyState } : msg
      )
    );
  };

  const handleApply = (messageId: string, changes: AiElementChange[]) => {
    setApplyState(messageId, 'applying');
    applyChanges(
      { reportId: report.id, body: mapAiChangesToApplyBody(changes, report) },
      {
        onSuccess: () => {
          if (isMountedRef.current) setApplyState(messageId, 'applied');
        },
        onError: () => {
          if (isMountedRef.current) setApplyState(messageId, 'idle');
        },
      }
    );
  };

  const sendUserMessage = (content: string) => {
    if (isPending) return;

    const userMessage: ChatMessage = { role: 'user', content, id: crypto.randomUUID() };
    const history = toHistory([...messages, userMessage]);
    setMessages(prev => [...prev, userMessage]);
    scrollToBottom();

    generate(
      { reportId: report.id, messages: history },
      {
        onSuccess: data => {
          if (!isMountedRef.current) return;
          const { clarification } = data;
          if (data.kind === 'CLARIFICATION' && clarification) {
            setMessages(prev => [
              ...prev,
              {
                role: 'clarification',
                message: data.message,
                questions: clarification.questions,
                assistantMessage: data.assistantMessage,
                id: crypto.randomUUID(),
              },
            ]);
          } else if (data.report) {
            const generated = data.report;
            setMessages(prev => [
              ...prev,
              {
                role: 'report',
                message: data.message,
                report: generated,
                assistantMessage: data.assistantMessage,
                applyState: 'idle',
                id: crypto.randomUUID(),
              },
            ]);
          }
          scrollToBottom();
        },
        onError: () => {
          if (!isMountedRef.current) return;
          setMessages(prev => [
            ...prev,
            { role: 'error', content: t('ai.errorMessage'), id: crypto.randomUUID() },
          ]);
          scrollToBottom();
        },
      }
    );
  };

  const handleSubmit = () => {
    const trimmed = input.trim();
    if (!trimmed || isPending) return;
    setInput('');
    sendUserMessage(trimmed);
  };

  return (
    <motion.div
      drag
      dragControls={dragControls}
      dragListener={false}
      dragMomentum={false}
      dragElastic={0}
      className="fixed bottom-6 left-6 z-50 flex flex-col overflow-hidden rounded-xl border bg-card shadow-xl"
      style={{ width: size.width, height: isMinimized ? 'auto' : size.height }}
    >
      {/* Header — drag handle */}
      <div
        className="flex shrink-0 cursor-grab items-center gap-1.5 px-3 py-2.5 active:cursor-grabbing"
        onPointerDown={e => dragControls.start(e)}
      >
        <GripHorizontal className="h-3.5 w-3.5 shrink-0 text-muted-foreground/50" />
        <Sparkles className="h-4 w-4 shrink-0 text-primary" />
        <span className="flex-1 select-none text-sm font-medium">{t('ai.panelTitle')}</span>
        <Button
          variant="ghost"
          size="icon"
          className="h-6 w-6"
          onPointerDown={e => e.stopPropagation()}
          onClick={() => setIsMinimized(prev => !prev)}
        >
          {isMinimized ? (
            <ChevronUp className="h-3.5 w-3.5" />
          ) : (
            <ChevronDown className="h-3.5 w-3.5" />
          )}
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="h-6 w-6"
          onPointerDown={e => e.stopPropagation()}
          onClick={onClose}
        >
          <X className="h-3.5 w-3.5" />
        </Button>
      </div>

      {!isMinimized && <Separator />}

      {/* Messages */}
      <div className={cn('flex-1 overflow-y-auto overflow-x-hidden', isMinimized && 'hidden')}>
        <div className="p-3">
          {messages.length === 0 && (
            <div className="flex flex-col items-center gap-2 py-8 text-center">
              <Sparkles className="h-8 w-8 text-muted-foreground/40" />
              <p className="text-sm font-medium text-muted-foreground">{t('ai.emptyTitle')}</p>
              <p className="text-xs text-muted-foreground">{t('ai.emptyHint')}</p>
            </div>
          )}

          <MessageGroup>
            {messages.map(msg => {
              if (msg.role === 'user') {
                return (
                  <Message key={msg.id} align="end">
                    <MessageContent>
                      <Bubble variant="secondary" align="end">
                        <BubbleContent className="whitespace-pre-wrap">{msg.content}</BubbleContent>
                      </Bubble>
                    </MessageContent>
                  </Message>
                );
              }

              if (msg.role === 'error') {
                return (
                  <Message key={msg.id}>
                    <MessageContent>
                      <Bubble variant="destructive">
                        <BubbleContent>{msg.content}</BubbleContent>
                      </Bubble>
                    </MessageContent>
                  </Message>
                );
              }

              if (msg.role === 'clarification') {
                const isLast = messages[messages.length - 1]?.id === msg.id;
                return (
                  <Message key={msg.id}>
                    <MessageAvatar className="h-7 w-7">
                      <Sparkles className="h-3.5 w-3.5 text-primary" />
                    </MessageAvatar>
                    <MessageContent>
                      <MessageHeader>{t('ai.assistantLabel')}</MessageHeader>
                      <BubbleGroup>
                        <Bubble variant="muted">
                          <BubbleContent>{msg.message}</BubbleContent>
                        </Bubble>
                        <ReportAiClarificationCard
                          questions={msg.questions}
                          isActive={isLast}
                          disabled={isPending}
                          onSubmit={sendUserMessage}
                        />
                      </BubbleGroup>
                    </MessageContent>
                  </Message>
                );
              }

              const { changes, customFormulas, plan } = msg.report;
              const hasContent = changes.length > 0 || customFormulas.length > 0;

              return (
                <Message key={msg.id}>
                  <MessageAvatar className="h-7 w-7">
                    <Sparkles className="h-3.5 w-3.5 text-primary" />
                  </MessageAvatar>
                  <MessageContent>
                    <MessageHeader>{t('ai.assistantLabel')}</MessageHeader>
                    {!hasContent ? (
                      <Bubble variant="muted">
                        <BubbleContent className="text-muted-foreground">
                          {t('ai.noElements')}
                        </BubbleContent>
                      </Bubble>
                    ) : (
                      <BubbleGroup>
                        <Bubble variant="muted">
                          <BubbleContent>{msg.message}</BubbleContent>
                        </Bubble>
                        <ReportAiPlanSummary plan={plan} />
                        {customFormulas.length > 0 && (
                          <>
                            <p className="px-1 text-xs font-medium text-muted-foreground">
                              {t('ai.customFormulas')}
                            </p>
                            {customFormulas.map(formula => (
                              <AiFormulaCard
                                key={formula.key}
                                formula={formula}
                                savedKeys={savedFormulaKeys}
                                onSaved={key =>
                                  setSavedFormulaKeys(prev => new Set([...prev, key]))
                                }
                              />
                            ))}
                          </>
                        )}
                        {changes.length > 0 && (
                          <>
                            <p className="px-1 text-xs font-medium text-muted-foreground">
                              {t('ai.proposedChanges')}
                            </p>
                            {changes.map((change, idx) => (
                              <AiChangeCard
                                key={`${change.action}-${change.elementId ?? 'new'}-${idx}`}
                                change={change}
                                report={report}
                              />
                            ))}
                            <AiApplyButton
                              applyState={msg.applyState}
                              onApply={() => handleApply(msg.id, changes)}
                            />
                          </>
                        )}
                      </BubbleGroup>
                    )}
                  </MessageContent>
                </Message>
              );
            })}

            {isPending && (
              <Message>
                <MessageAvatar className="h-7 w-7">
                  <Sparkles className="h-3.5 w-3.5 text-primary" />
                </MessageAvatar>
                <MessageContent>
                  <Bubble variant="muted">
                    <BubbleContent>
                      <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
                    </BubbleContent>
                  </Bubble>
                </MessageContent>
              </Message>
            )}
          </MessageGroup>

          <div ref={bottomRef} />
        </div>
      </div>

      {!isMinimized && <Separator />}

      {/* Input */}
      <div className={cn('shrink-0 p-3', isMinimized && 'hidden')}>
        <div className="flex items-end gap-2">
          <Textarea
            ref={inputRef}
            rows={1}
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => {
              // Don't send while an IME is composing (e.g. confirming a Japanese or Chinese word with Enter).
              if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
                e.preventDefault();
                handleSubmit();
              }
            }}
            placeholder={t('ai.inputPlaceholder')}
            className="min-h-8 resize-none py-1.5 text-sm"
            disabled={isPending}
          />
          <Button
            size="sm"
            className="h-8 w-8 shrink-0 p-0"
            onClick={handleSubmit}
            disabled={!input.trim() || isPending}
          >
            {isPending ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <SendHorizontal className="h-3.5 w-3.5" />
            )}
          </Button>
        </div>
        <p className="mt-1.5 text-xs text-muted-foreground">{t('ai.inputHint')}</p>
      </div>

      {/* Resize handle — bottom-right corner */}
      {!isMinimized && (
        <div
          className="absolute bottom-0 right-0 h-4 w-4 cursor-se-resize"
          onPointerDown={handleResizeMouseDown}
          style={{
            background: 'linear-gradient(135deg, transparent 50%, hsl(var(--border)) 50%)',
          }}
        />
      )}
    </motion.div>
  );
};
